/* ------------------------------------------------------------------
   KORE - product gallery: wrap-around scrolling

   The gallery is a native overflow scroller with scroll-snap, not a
   JS carousel, so it simply stops at both ends. This makes it loop:
   swipe left from the first image and you land on the last.

   HOW, and why it is done this way:

   A clone of the last slide is prepended and a clone of the first is
   appended. The scroller starts on the real first slide, so the clone
   sits just off screen to its left. Swiping left lands on that clone,
   which is pixel-identical to the last slide - and only ONCE THE
   SCROLL HAS SETTLED does the script silently jump to the real last
   slide.

   Repositioning on every scroll event instead would be the obvious
   approach and it is the wrong one: writing scrollLeft in the middle
   of iOS momentum scrolling cancels the momentum, which is exactly
   the stutter this is supposed to avoid. Waiting for the scroll to
   finish means the jump happens while nothing is moving, and the user
   cannot see it because the two slides are the same image.

   Mobile only. On desktop the arrows are visible and the theme's own
   slider logic owns the behaviour.
   ------------------------------------------------------------------ */

(function () {
  'use strict';

  var MOBILE = 768;
  var SETTLE_MS = 120;
  /* Switched off 2026-10-06 at the owner's request: the gallery now starts on photo 1,
     cannot be swiped left, and stops at the last photo. The copy of the last slide
     (the 90-day guarantee) placed before photo 1 was suspected of glitching and of
     recordings opening on that photo. Set to true to bring the wrap-around back. */
  var LOOP = false;

  /* Reposition the scroller with nothing animating.

     The theme sets scroll-behavior: smooth on this list, so a plain
     `scrollLeft = x` ANIMATES - which made the seam visible and, because
     an inline non-important declaration loses to it, silently did nothing
     at all on the first attempt. scrollTo with behavior:'instant'
     overrides the CSS per call; the fallback forces it with !important
     for browsers that do not accept 'instant'.

     Snap is lifted for the write too, or the browser animates its own
     correction straight afterwards. */
  function setTo(list, x) {
    var snap = list.style.getPropertyValue('scroll-snap-type');
    var prio = list.style.getPropertyPriority('scroll-snap-type');
    list.style.setProperty('scroll-snap-type', 'none', 'important');
    try {
      list.scrollTo({ left: x, behavior: 'instant' });
    } catch (e) {
      list.style.setProperty('scroll-behavior', 'auto', 'important');
      list.scrollLeft = x;
      list.style.removeProperty('scroll-behavior');
    }
    void list.offsetHeight;
    if (snap) list.style.setProperty('scroll-snap-type', snap, prio);
    else list.style.removeProperty('scroll-snap-type');
  }

  function init(list) {
    if (!list || list.dataset.koreLoop === 'on') return;
    var slides = Array.prototype.filter.call(list.children, function (el) {
      return el.tagName === 'LI';
    });
    if (slides.length < 2) return;

    var head = slides[0].cloneNode(true);
    var tail = slides[slides.length - 1].cloneNode(true);
    [head, tail].forEach(function (c) {
      c.setAttribute('aria-hidden', 'true');
      c.dataset.koreClone = '1';
      // clones must never be reachable by keyboard or announced twice
      Array.prototype.forEach.call(c.querySelectorAll('a,button,input'), function (n) {
        n.setAttribute('tabindex', '-1');
      });
    });

    list.insertBefore(tail, slides[0]);
    list.appendChild(head);
    list.dataset.koreLoop = 'on';

    function pitch() {
      var a = slides[0].getBoundingClientRect();
      var b = slides[1].getBoundingClientRect();
      return Math.round(b.left - a.left) || Math.round(a.width);
    }

    function span() {
      return pitch() * slides.length;
    }

    setTo(list, pitch());

    var timer = null;
    function settled() {
      var p = pitch();
      var s = span();
      var x = list.scrollLeft;
      if (x < p * 0.5) {
        jump(x + s);
      } else if (x > s + p * 0.5) {
        jump(x - s);
      }
    }

    /* Land exactly on a slide, not merely `span` away from where we were.

       The appended clone can never be scrolled to in full: maximum scroll
       is content width minus viewport, and with the peek layout the last
       slide always stops short. Subtracting `span` from a clamped position
       therefore lands slightly left of the real slide, so the offset is
       rounded to the nearest whole slide. */
    function jump(to) {
      var p = pitch();
      setTo(list, Math.max(0, Math.round(to / p) * p));
    }

    list.addEventListener('scroll', function () {
      if (timer) clearTimeout(timer);
      timer = setTimeout(settled, SETTLE_MS);
    }, { passive: true });
  }

  function run() {
    if (!LOOP || window.innerWidth > MOBILE) return;
    document.querySelectorAll('.product__media-list').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();

/* ------------------------------------------------------------------
   KORE - trust tiles: lift on tap

   The tiles lift on :hover, which a finger cannot do. On devices with
   no hover, a tap toggles .is-up on the tile (styled the same as
   :hover); tapping another tile moves the lift, tapping anywhere else
   drops it. Nothing runs on a mouse device, where :hover already works.
   ------------------------------------------------------------------ */
(function () {
  'use strict';
  if (!window.matchMedia || !window.matchMedia('(hover: none)').matches) return;

  function drop(except) {
    document.querySelectorAll('.kore-trust .kt-tile.is-up').forEach(function (t) {
      if (t !== except) t.classList.remove('is-up');
    });
  }

  document.addEventListener('click', function (e) {
    var tile = e.target.closest && e.target.closest('.kore-trust .kt-tile');
    if (tile) {
      tile.classList.toggle('is-up');
      drop(tile);
    } else {
      drop(null);
    }
  }, { passive: true });
})();

/* ------------------------------------------------------------------
   KORE - sticky buy bar

   The theme's sticky add-to-cart strip is hidden in CSS; this bar
   takes its job. It slides up once the real buy button has scrolled
   above the top of the screen and slides away again if the visitor
   scrolls back up to it. It also fades out while the reviews list is
   on screen, so it never sits over "Load More Reviews".
   ------------------------------------------------------------------ */
(function () {
  'use strict';
  function run() {
    var bar = document.querySelector('.kore-stickybar');
    var atc = document.querySelector('.product-form__buttons');
    if (!bar || !atc || !('IntersectionObserver' in window)) return;
    var passed = false, inReviews = false;
    function update() {
      bar.classList.toggle('is-on', passed && !inReviews);
    }
    new IntersectionObserver(function (entries) {
      var e = entries[0];
      // "above the viewport" = not on screen and its box sits above the top
      passed = !e.isIntersecting && e.boundingClientRect.bottom < 0;
      update();
    }, { threshold: 0 }).observe(atc);

    // hide from the point the reviews list reaches the lower part of the
    // screen until it has scrolled away again
    var reviews = document.querySelectorAll('.kore-reviews, .kore-footer');
    if (reviews.length) {
      var seen = new Set();
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) seen.add(e.target); else seen.delete(e.target);
        });
        inReviews = seen.size > 0;
        update();
      }, { threshold: 0, rootMargin: '0px 0px -25% 0px' });
      reviews.forEach(function (r) { io.observe(r); });
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();

/* ------------------------------------------------------------------
   KORE - timeline draw

   The "What to Expect" timeline starts dim; the first time its first
   step is properly on screen the markers light up one after another
   and the line runs between them (timing lives in the CSS). It plays
   once and never resets. If the page loads already scrolled past it,
   it is shown finished. Reduced-motion visitors just get it finished.
   ------------------------------------------------------------------ */
(function () {
  'use strict';
  function run() {
    var line = document.querySelector('.kore-timeline .kt-line');
    if (!line || !('IntersectionObserver' in window)) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var first = line.querySelector('.kt-step');
    if (!first) return;
    line.classList.add('kt-anim');
    var io = new IntersectionObserver(function (entries) {
      var e = entries[0];
      if (e.isIntersecting || e.boundingClientRect.top < 0) {
        line.classList.add('is-in');
        io.disconnect();
      }
    }, { threshold: 0.6 });
    io.observe(first);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();

/* ------------------------------------------------------------------
   KORE - results count-up

   Each stat counts from 0 to its value, fast (about a second, easing
   out, a short stagger between the three), the first time the stats
   come into view. Plays once. The final numbers are in the HTML, so
   without JS - or if the page loads already scrolled past - they show
   as written.
   ------------------------------------------------------------------ */
(function () {
  'use strict';
  function run() {
    var box = document.querySelector('.kore-results .kr-stats');
    if (!box || !('IntersectionObserver' in window)) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var nums = Array.prototype.slice.call(box.querySelectorAll('.kr-count'));
    if (!nums.length) return;
    nums.forEach(function (n) { n.textContent = '0'; });

    function count(el, delay) {
      var to = parseInt(el.getAttribute('data-to'), 10) || 0;
      var DUR = 1000;
      setTimeout(function () {
        var t0 = null;
        function step(ts) {
          if (t0 === null) t0 = ts;
          var p = Math.min(1, (ts - t0) / DUR);
          var e = 1 - Math.pow(1 - p, 3);           // ease-out cubic
          el.textContent = String(Math.round(to * e));
          if (p < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
      }, delay);
    }

    var io = new IntersectionObserver(function (entries) {
      var e = entries[0];
      if (e.isIntersecting) {
        nums.forEach(function (n, i) { count(n, i * 150); });
        io.disconnect();
      } else if (e.boundingClientRect.top < 0) {
        nums.forEach(function (n) { n.textContent = n.getAttribute('data-to'); });
        io.disconnect();
      }
    }, { threshold: 0.35 });
    io.observe(box);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();

/* ------------------------------------------------------------------
   KORE - no cart, checkout only

   The store has no cart: Add to Cart sends the shopper straight to
   checkout with exactly what they just picked.

   - Every time a storefront page loads (including coming Back from
     checkout), anything left in the cart is cleared, so nothing from an
     earlier visit carries into the next checkout and they choose again.
   - Add to Cart waits for that clear to finish before the bundler's own
     add goes through, then goes to /checkout once the add has landed.
     Three independent signals trigger the redirect, so it works however
     the bundler loads on a given phone: the cart calls going quiet, the
     bundler opening its drawer, or the bundler loading the /cart page.
   - The header cart icon and the cart drawer are hidden (CSS section 27),
     and /cart itself redirects: to checkout straight after an Add to
     Cart, otherwise back to the product.

   The bundler's add (/cart/add.js) and its bundle attributes
   (/cart/update.js) still run untouched - skipping them would lose the
   bundle pricing.
   ------------------------------------------------------------------ */
if (window.KORE_NO_CART) (function () {
  'use strict';
  var KEY = 'kore_atc_to_checkout';
  var PRODUCT = '/products/ginseng-ginkgo-biloba-capsules';
  var html = document.documentElement;
  var nativeFetch = window.fetch;
  if (!nativeFetch) return;

  function recentAtc() {
    try {
      var t = +sessionStorage.getItem(KEY) || 0;
      return t && Date.now() - t < 20000;
    } catch (e) { return false; }
  }

  // /cart: straight after Add to Cart -> checkout; otherwise -> product
  if (/^\/cart\/?$/.test(location.pathname)) {
    html.classList.add('kore-to-checkout');
    if (recentAtc()) {
      try { sessionStorage.removeItem(KEY); } catch (e) {}
      location.replace('/checkout');
    } else {
      nativeFetch.call(window, '/cart/clear.js', { method: 'POST', credentials: 'same-origin' })
        .catch(function () {})
        .then(function () { location.replace(PRODUCT); });
    }
    return;
  }

  // every other page: start from an empty cart
  var clearing = null;
  function clearCart() {
    clearing = nativeFetch.call(window, '/cart/clear.js', { method: 'POST', credentials: 'same-origin' })
      .catch(function () {});
    return clearing;
  }
  clearCart();
  // Back from checkout restores this page from the browser cache without
  // reloading it - clear again and undo the in-flight state
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) { disarm(); clearCart(); }
  });

  var armed = false, added = false, pending = 0, quiet = null, cap = null;

  function go() {
    if (!armed) return;
    armed = false;
    try { sessionStorage.removeItem(KEY); } catch (e) {}
    window.location.href = '/checkout';
  }
  function settle() {
    if (quiet) clearTimeout(quiet);
    if (added && pending === 0) quiet = setTimeout(go, 350);
  }
  function disarm() {
    armed = added = false;
    html.classList.remove('kore-to-checkout');
    if (cap) clearTimeout(cap);
    try { sessionStorage.removeItem(KEY); } catch (e) {}
  }

  // signal 1: watch the bundler's cart calls. An add is held until the
  // page-load clear has finished, so the clear can never wipe it.
  window.fetch = function (input) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var path = url.replace(location.origin, '');
    var isAddPath = /\/cart\/add(\.js)?/.test(path);
    var isCart = armed && /\/cart(\/(add|update|change))?(\.js)?(\?|$)/.test(path);
    var isAdd = armed && isAddPath;
    var self = this, args = arguments;
    var p = isAddPath && clearing
      ? clearing.then(function () { return nativeFetch.apply(self, args); })
      : nativeFetch.apply(this, arguments);
    if (isCart) {
      pending++;
      p.then(function (res) {
        pending--;
        if (isAdd) { if (res.ok) added = true; else { disarm(); return; } }
        settle();
      }, function () {
        pending--;
        if (isAdd) disarm(); else settle();
      });
    }
    return p;
  };

  // signal 2: the drawer opening means the add has finished
  function watchDrawer() {
    var d = document.getElementById('atlas-upsell-cart-drawer-main-container');
    if (!d || !('MutationObserver' in window)) return;
    new MutationObserver(function () {
      if (armed && d.classList.contains('is-open')) setTimeout(go, 400);
    }).observe(d, { attributes: true, attributeFilter: ['class'] });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchDrawer);
  else watchDrawer();

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.product-form__buttons .atc-button, button[name="atlas-bundler-add"]');
    if (!btn || btn.disabled) return;
    armed = true; added = false; pending = 0;
    html.classList.add('kore-to-checkout');
    try { sessionStorage.setItem(KEY, String(Date.now())); } catch (x) {}
    if (cap) clearTimeout(cap);
    // last resort: after 6s, go if the cart has anything in it
    cap = setTimeout(function () {
      if (!armed) return;
      if (added) return go();
      nativeFetch.call(window, '/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (c) { if (c && c.item_count > 0) go(); else disarm(); })
        .catch(disarm);
    }, 6000);
  }, true);
})();
